// @ts-nocheck
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '@way/theme-host/services/logger.service';
import { CommonModule } from '@angular/common';
import {
  PageHeaderComponent,
  NavigationItem,
  Logo,
  CTAButton,
} from '../shared/page-header/page-header.component';
import { PageFooterComponent } from '../shared/footer/footer.component';
import { resolveFooterQuickLinks } from '@way/theme-host/shared/utils/footer-quick-links.util';

export interface WebsiteElement {
  id: string;
  type:
    | 'header'
    | 'hero'
    | 'section'
    | 'testimonials'
    | 'services'
    | 'booking'
    | 'contact'
    | 'footer'
    | 'coupons'
    | 'packages'
    | 'fleets'
    | 'express-details';
  content: any;
  selected?: boolean;
}

@Component({
  selector: 'app-coupons-preview',
  standalone: true,
  imports: [CommonModule, PageHeaderComponent, PageFooterComponent],
  templateUrl: './coupons-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./coupons-preview.component.scss'],
})
export class CouponsPreviewComponent implements OnInit, OnChanges {
  @Input() pageData: any = null;
  @Input() sectionVisibility: Record<string, boolean> = {};
  @Input() pages: any[] = [];
  @Input() currentPageType = '';
  @Input() elements: WebsiteElement[] = [];
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Input() primaryColor: string | null = null; // Primary color from theme
  @Input() headingColor: string | null = null; // Heading color from branding
  @Input() paragraphColor: string | null = null; // Paragraph/description color from branding
  @Input() headingFont: string | null = null; // Heading font from branding
  @Input() bodyFont: string | null = null; // Body font from branding
  @Output() elementSelect = new EventEmitter<WebsiteElement>();
  @Output() navigationClick = new EventEmitter<string>();

  // Page data from JSON
  headerData: any = null;
  pageTitleData: any = null;
  couponCardData: any = null;
  instructionsSectionData: any = null;
  footerData: any = null;

  constructor(private logger: LoggerService) {}

  ngOnInit(): void {
    this.loadPageContent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageData'] && !changes['pageData'].firstChange) {
      this.loadPageContent();
    }
    // Update navigation when pages or currentPageType changes
    if ((changes['pages'] || changes['currentPageType']) && this.headerData) {
      this.updateHeaderNavigation();
    }
  }

  /**
   * Load coupons page content from this.pageData, extract sections (header, page-title, coupon_card, instructions_section, footer) and update component state and header navigation.
   * @example
   * loadPageContent()
   * undefined
   * @returns {{void}} No return value; updates component properties (headerData, pageTitleData, couponCardData, instructionsSectionData, footerData) and refreshes header navigation.
   **/
  private loadPageContent(): void {
    if (!this.pageData) return;

    this.logger.debug('🎟️ Loading Coupons page content:', this.pageData);

    // Extract sections from page data - handle both editor and preview formats
    // Editor format: pageData.page
    // Preview format: pageData.json_data.page
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    const sections = page?.sections || {};

    this.headerData = sections.header || page.header || {};
    this.pageTitleData =
      sections['page-title'] || sections.hero_section || page['page-title'] || {};
    this.couponCardData =
      sections.coupon_card ||
      sections.coupon_section?.coupon_card ||
      sections.coupon_section ||
      page.coupon_card ||
      {};
    this.instructionsSectionData =
      sections.instructions_section ||
      sections.instruction_section ||
      page.instructions_section ||
      page.instruction_section ||
      {};
    this.footerData = sections.footer || page.footer || {};

    this.logger.debug('Coupons page sections loaded:', {
      header: this.headerData,
      hero: this.pageTitleData,
      coupon: this.couponCardData,
      instructions: this.instructionsSectionData,
      footer: this.footerData,
      pageInstructions: page?.instructions,
    });

    // Update header navigation to match current pages
    this.updateHeaderNavigation();
  }

  /**
   * Update header navigation from available pages and ensure a default CTA button exists when missing.
   * @example
   * updateHeaderNavigation()
   * undefined
   * @returns {void} Updates this.headerData in place; does not return a value.
   **/
  private updateHeaderNavigation(): void {
    if (!this.headerData) this.headerData = {};

    // Generate navigation from pages if pages are provided
    // Filter out inactive pages (visible: false means active: false)
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      this.headerData.navigation = activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: page.pageType === this.currentPageType,
      }));
      this.logger.debug('Updated header navigation for Coupons:', this.headerData.navigation);
    }

    // Ensure CTA button exists
    if (!this.headerData.ctaButton && !this.headerData.button) {
      this.headerData.ctaButton = {
        text: '',
        link: '#booking',
        backgroundColor: '#1B5BFF',
        textColor: '#FFFFFF',
      };
    }
  }

  /**
   * Check if a section should be visible based on section visibility settings
   */
  isSectionVisible(sectionKey: string): boolean {
    if (!this.sectionVisibility || Object.keys(this.sectionVisibility).length === 0) {
      return true;
    }
    const aliases: Record<string, string[]> = {
      'page-title': ['page-title', 'hero_section', 'hero'],
      coupon_card: ['coupon_card', 'coupon_section'],
      instructions_section: ['instructions_section', 'instruction_section'],
    };
    const keysToCheck = aliases[sectionKey] || [sectionKey];
    return keysToCheck.every(key => this.sectionVisibility[key] !== false);
  }

  /**
   * Get footer quick links from active pages
   * Filters out pages where visible === false (active === false)
   */
  getFooterQuickLinks(): { label: string; link: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const storedLinks = Array.isArray(footerContent?.quickLinks) ? footerContent.quickLinks : [];
    return resolveFooterQuickLinks(storedLinks, this.pages);
  }

  /**
   * Returns normalized social links from the footer content as an array of objects with platform, url and icon.
   * @example
   * etFooterSocialLinks()
   * [{ platform: "facebook", url: "https://facebook.com/example", icon: "facebook" }]
   * @returns {{Array<{ platform: string; url: string; icon: string }}} An array of social link objects; empty array if none are present.
   **/
  getFooterSocialLinks(): { platform: string; url: string; icon: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.socialLinks && Array.isArray(footerContent.socialLinks)) {
      return footerContent.socialLinks.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }
    return [];
  }

  getFooterSocialLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    return footerContent?.socialLinksTitle || 'Connect with us';
  }

  getFooterQuickLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    return footerContent?.quickLinksTitle || 'Quick links';
  }

  getFooterCopyright(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.copyright) {
      return footerContent.copyright;
    }
    return `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;
  }

  /**
   * Retrieve the footer logo (text and image), falling back to the header logo or a default.
   * @example
   * etFooterLogo()
   * { text: 'way', image: 'path/to/logo.png' }
   * @returns {{text?: string, image?: string}|undefined} Return an object with optional text and image properties, or undefined if no logo information is available.
   */
  getFooterLogo(): { text?: string; image?: string } | undefined {
    // Get logo from footer element content
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;

    if (footerContent?.logo) {
      const logo = footerContent.logo;
      return {
        text: logo.text || 'way',
        image: logo.image || logo.url || '',
      };
    }

    // Fallback to header logo if footer logo not available
    const headerLogo = this.getHeaderLogo();
    if (headerLogo && headerLogo.image) {
      return {
        text: headerLogo.text,
        image: headerLogo.image,
      };
    }
    return { text: 'way' };
  }

  getFooterBackgroundColor(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.backgroundColor) {
      return footerContent.backgroundColor;
    }
    return '#000920';
  }

  getHeaderElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'header');
  }

  getFooterElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'footer');
  }

  /**
   * Retrieve the header logo object (containing image, text, and link) from the component's header element.
   * @example
   * etHeaderLogo()
   * { image: 'logo.png', text: 'Logo', link: '/' }
   * @returns {{Logo}} The header logo object with keys: image (string), text (string), and link (string).
   **/
  getHeaderLogo(): Logo {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.logo) {
      return {
        image: headerContent.logo.image || headerContent.logo.url || '',
        text: headerContent.logo.text || 'Logo',
        link: headerContent.logo.link || '/',
      };
    }
    return { text: 'Logo', link: '/' };
  }

  /**
   * Build header navigation items from header content or fallback pages, ensuring each item has a normalized pageType and link.
   * @example
   * this.etHeaderNavigation()
   * [
   *   { label: 'Home', link: '#home', pageType: 'home', active: true },
   *   { label: 'Express Washes', link: '#express-wash-packages', pageType: 'express-wash-packages', active: false }
   * ]
   * @returns {NavigationItem[]} Array of navigation items with pageType and link normalized.
   */
  getHeaderNavigation(): NavigationItem[] {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.navigation && Array.isArray(headerContent.navigation)) {
      // Ensure navigation items have pageType set
      return headerContent.navigation.map((nav: any) => {
        // If pageType is not set, try to extract it from link or find matching page
        if (!nav.pageType) {
          const matchingPage = this.pages?.find(
            (p: any) =>
              p.name === nav.label ||
              (p as any).pageType === nav.link?.replace(/^#+|\/+$/g, '').replace(/^\/+/, '')
          );
          if (matchingPage) {
            nav.pageType = (matchingPage as any).pageType;
          } else {
            // Extract from link
            const normalizedLink = (nav.link || '')
              .replace(/^#+|\/+$/g, '')
              .replace(/^\/+/, '')
              .toLowerCase();
            if (
              normalizedLink === 'expresswashes' ||
              normalizedLink === 'express-washes' ||
              normalizedLink.includes('express-wash')
            ) {
              nav.pageType = 'express-wash-packages';
            } else if (
              normalizedLink === 'expressdetails' ||
              normalizedLink === 'express-details' ||
              normalizedLink.includes('express-detail') ||
              normalizedLink.includes('express-auto')
            ) {
              nav.pageType = 'express-auto-details';
            } else if (
              normalizedLink === 'fleets' ||
              normalizedLink === 'fleet' ||
              normalizedLink.includes('fleet')
            ) {
              nav.pageType = 'fleet-management';
            } else if (
              normalizedLink === 'coupons' ||
              normalizedLink === 'coupon' ||
              normalizedLink.includes('coupon')
            ) {
              nav.pageType = 'coupons';
            } else if (
              normalizedLink === '' ||
              normalizedLink === 'home' ||
              normalizedLink === '/'
            ) {
              nav.pageType = 'home';
            } else {
              nav.pageType = normalizedLink || 'home';
            }
          }
        }
        return nav;
      });
    }
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      return activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        pageType: (page as any).pageType || 'home', // Ensure pageType is set
        active: page.pageType === this.currentPageType,
      }));
    }
    return [];
  }

  /**
   * Returns the header CTA button configuration from the component header content, using content values or sensible defaults.
   * @example
   * etHeaderCTAButton()
   * { text: 'Book Wash', link: '#', backgroundColor: '#1B5BFF', textColor: '#FFFFFF' }
   * @returns {CTAButton|null} CTAButton object with text, link, backgroundColor and textColor, or null if not available.
   */
  getHeaderCTAButton(): CTAButton | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    const primaryColor = this.getPrimaryColor();
    if (headerContent?.ctaButton) {
      return {
        text: headerContent.ctaButton.text || headerContent.ctaButton.label || 'Book Wash',
        link: headerContent.ctaButton.link || '#',
        // Use button's backgroundColor if provided, otherwise fall back to primaryColor
        backgroundColor: headerContent.ctaButton.backgroundColor || primaryColor || '#1B5BFF',
        textColor: headerContent.ctaButton.textColor || '#FFFFFF',
      };
    }
    return {
      text: 'Book Wash',
      link: '#',
      backgroundColor: primaryColor,
      textColor: '#FFFFFF',
    };
  }

  getHeaderBackgroundColor(): string | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.backgroundColor) {
      return headerContent.backgroundColor as string;
    }
    return null;
  }

  /**
   * Get primary color from theme or use default
   */
  getPrimaryColor(): string {
    return this.primaryColor || '#1B5BFF';
  }

  /**
   * Get heading color from branding or use default
   */
  getHeadingColor(): string {
    return this.headingColor || '#15172E';
  }

  /**
   * Get paragraph color from branding or use default
   */
  getParagraphColor(): string {
    return this.paragraphColor || '#6B7280';
  }

  /**
   * Get heading font from branding or use default
   */
  getHeadingFont(): string {
    return this.headingFont || 'Manrope';
  }

  /**
   * Get body font from branding or use default
   */
  getBodyFont(): string {
    return this.bodyFont || 'Inter';
  }

  /**
   * Get hero title text
   */
  getPageTitle(): string {
    if (this.pageTitleData?.heading?.text) {
      return this.pageTitleData.heading.text;
    }
    if (this.pageTitleData?.title) {
      if (typeof this.pageTitleData.title === 'string') {
        return this.pageTitleData.title;
      } else if (this.pageTitleData.title?.label) {
        return this.pageTitleData.title.label;
      }
    }
    // Check page-level title
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.title) {
      if (typeof page.title === 'string') {
        return page.title;
      } else if (page.title?.label) {
        return page.title.label;
      }
    }
    // Check section content
    if (page?.section?.content?.heading?.text) {
      return page.section.content.heading.text;
    }
    return '';
  }

  /**
   * Get hero subtitle/description text
   */
  getPageSubtitle(): string {
    if (this.pageTitleData?.paragraph?.text) {
      return this.pageTitleData.paragraph.text;
    }
    if (this.pageTitleData?.description) {
      return this.pageTitleData.description;
    } else if (this.pageTitleData?.subtitle) {
      return this.pageTitleData.subtitle;
    }
    // Check page-level description
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.description) {
      return page.description;
    }
    // Check section content
    if (page?.section?.content?.paragraph?.text) {
      return page.section.content.paragraph.text;
    }
    return '';
  }

  /**
   * Get title styles for ngStyle binding
   */
  getTitleStyles(): any {
    const page = this.pageData?.page || this.pageData;
    // Check section content heading style
    if (page?.section?.content?.heading?.style) {
      const headingStyle = page.section.content.heading.style;
      const styles: any = {};
      if (headingStyle.fontWeight) styles['font-weight'] = headingStyle.fontWeight;
      if (headingStyle.fontStyle) styles['font-style'] = headingStyle.fontStyle;
      if (headingStyle.textDecoration) styles['text-decoration'] = headingStyle.textDecoration;
      if (headingStyle.textAlign || headingStyle.alignment)
        styles['text-align'] = headingStyle.textAlign || headingStyle.alignment;
      return styles;
    }
    // Check pageTitleData
    if (this.pageTitleData?.title?.style?.text_style) {
      const titleStyleObj = this.pageTitleData.title.style.text_style;
      const styles: any = {};
      if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
      if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
      if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
      if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
      return styles;
    }
    return {};
  }

  /**
   * Get coupon section element for selection
   */
  getCouponSectionElement(): WebsiteElement | undefined {
    return this.elements.find(
      el =>
        el.type === 'coupons' ||
        el.id === 'coupons' ||
        (el as any).originalKey === 'page-title' ||
        (el as any).originalKey === 'coupon_card' ||
        (el as any).originalKey === 'instructions_section'
    );
  }

  /**
   * Handle hero section click
   */
  onPageHeaderSectionClick(event: Event): void {
    event.stopPropagation();
    const couponElement: WebsiteElement = {
      id: 'coupons',
      type: 'coupons',
      content: {
        ...(this.pageTitleData || {}),
        title: this.getPageTitle(),
        subtitle: this.getPageSubtitle(),
        couponColor:
          this.couponCardData?.color || this.couponCardData?.backgroundColor || '#1B5BFF',
        instructionHeading: this.getInstructionHeading(),
        instructionParagraph:
          this.instructionsSectionData?.description ||
          this.instructionsSectionData?.paragraph ||
          '',
      },
      selected: true,
      originalKey: 'page-title',
    } as any;
    this.logger.debug('🎟️ Hero section clicked, emitting element:', couponElement);
    this.elementSelect.emit(couponElement);
  }

  /**
   * Handle coupon card section click
   */
  onCouponCardClick(event: Event): void {
    event.stopPropagation();
    const couponElement: WebsiteElement = {
      id: 'coupons',
      type: 'coupons',
      content: {
        ...(this.pageTitleData || {}),
        title: this.getPageTitle(),
        subtitle: this.getPageSubtitle(),
        couponColor:
          this.couponCardData?.color || this.couponCardData?.backgroundColor || '#1B5BFF',
        instructionHeading: this.getInstructionHeading(),
        instructionParagraph:
          this.instructionsSectionData?.description ||
          this.instructionsSectionData?.paragraph ||
          '',
      },
      selected: true,
      originalKey: 'coupon_card',
    } as any;
    this.logger.debug('🎟️ Coupon card clicked, emitting element:', couponElement);
    this.elementSelect.emit(couponElement);
  }

  /**
   * Handle instructions section click
   */
  onInstructionsSectionClick(event: Event): void {
    event.stopPropagation();
    const couponElement: WebsiteElement = {
      id: 'coupons',
      type: 'coupons',
      content: {
        ...(this.pageTitleData || {}),
        title: this.getPageTitle(),
        subtitle: this.getPageSubtitle(),
        couponColor:
          this.couponCardData?.color || this.couponCardData?.backgroundColor || '#1B5BFF',
        instructionHeading: this.getInstructionHeading(),
        instructionParagraph:
          this.instructionsSectionData?.description ||
          this.instructionsSectionData?.paragraph ||
          '',
      },
      selected: true,
      originalKey: 'instructions_section',
    } as any;
    this.logger.debug('🎟️ Instructions section clicked, emitting element:', couponElement);
    this.elementSelect.emit(couponElement);
  }

  /**
   * Get instruction heading text
   */
  getInstructionHeading(): string {
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;

    if (this.instructionsSectionData?.heading?.text) {
      return this.instructionsSectionData.heading.text;
    }
    if (page?.sections?.instruction_section?.heading?.text) {
      return page.sections.instruction_section.heading.text;
    }
    if (page?.instructions?.heading?.text) {
      return page.instructions.heading.text;
    }

    // Priority 2: Check instructionsSectionData
    if (this.instructionsSectionData?.title) {
      if (typeof this.instructionsSectionData.title === 'string') {
        return this.instructionsSectionData.title;
      } else if (this.instructionsSectionData.title?.label) {
        return this.instructionsSectionData.title.label;
      }
    }

    // No hardcoded fallback - return empty string
    return '';
  }

  /**
   * Get instruction paragraph text
   */
  getInstructionParagraph(): string {
    // Priority 1: Check page.instructions.paragraph.text (API structure)
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.instructions?.paragraph?.text) {
      return page.instructions.paragraph.text;
    }

    // Priority 2: Check instructionsSectionData
    if (this.instructionsSectionData?.description) {
      return this.instructionsSectionData.description;
    } else if (this.instructionsSectionData?.paragraph) {
      return this.instructionsSectionData.paragraph;
    }

    // No hardcoded fallback - return empty string
    return '';
  }

  /**
   * Get promo code from API data
   */
  getPromoCode(): string {
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    const couponSection = page?.sections?.coupon_section || page?.section?.coupon || {};
    const nestedCard = couponSection.coupon_card || this.couponCardData || {};

    if (nestedCard.promo_code?.text) {
      return nestedCard.promo_code.text;
    }
    if (couponSection.promoCode?.display) {
      return couponSection.promoCode.display;
    }

    // Fallback to other possible locations
    if (this.couponCardData?.promoCode) {
      if (typeof this.couponCardData.promoCode === 'string') {
        return this.couponCardData.promoCode;
      } else if (this.couponCardData.promoCode.code) {
        return this.couponCardData.promoCode.code;
      } else if (this.couponCardData.promoCode.text) {
        return this.couponCardData.promoCode.text;
      } else if (this.couponCardData.promoCode.display) {
        return this.couponCardData.promoCode.display;
      }
    }

    if (couponSection.promoCode) {
      if (typeof couponSection.promoCode === 'string') {
        return couponSection.promoCode;
      } else if (couponSection.promoCode.code) {
        return couponSection.promoCode.code;
      } else if (couponSection.promoCode.text) {
        return couponSection.promoCode.text;
      }
    }

    return '';
  }

  /**
   * Get wash types from API data
   */
  getWashTypes(): string[] {
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    const couponSection = page?.sections?.coupon_section || page?.section?.coupon || {};
    const nestedCard = couponSection.coupon_card || this.couponCardData || {};

    if (
      nestedCard.applicable_washes?.packages &&
      Array.isArray(nestedCard.applicable_washes.packages)
    ) {
      return nestedCard.applicable_washes.packages
        .map((pkg: any) => pkg.name || pkg)
        .filter((name: string) => name);
    }
    if (couponSection.categories && Array.isArray(couponSection.categories)) {
      return couponSection.categories
        .map((cat: any) => cat.name || cat)
        .filter((name: string) => name);
    }

    // Fallback to other possible locations
    if (this.couponCardData?.washTypes && Array.isArray(this.couponCardData.washTypes)) {
      return this.couponCardData.washTypes;
    }
    if (this.couponCardData?.washes && Array.isArray(this.couponCardData.washes)) {
      return this.couponCardData.washes;
    }
    if (
      this.couponCardData?.availableWashes &&
      Array.isArray(this.couponCardData.availableWashes)
    ) {
      return this.couponCardData.availableWashes;
    }

    if (couponSection.washTypes && Array.isArray(couponSection.washTypes)) {
      return couponSection.washTypes;
    }
    if (couponSection.washes && Array.isArray(couponSection.washes)) {
      return couponSection.washes;
    }
    if (couponSection.availableWashes && Array.isArray(couponSection.availableWashes)) {
      return couponSection.availableWashes;
    }

    return [];
  }

  onHeaderClick(): void {
    const headerElement = this.getHeaderElement();
    if (headerElement) {
      this.onElementClick(headerElement);
    }
  }

  onElementClick(element: WebsiteElement): void {
    if (!element) return;
    this.elementSelect.emit(element);
  }

  onNavigationClick(pageType: string): void {
    this.navigationClick.emit(pageType);
  }

  /**
   * Get instruction steps from API data
   */
  getInstructionSteps(): { number: number; title: string; description: string }[] {
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    const mapSteps = (steps: any[]) =>
      steps.map((step: any) => ({
        number: step.step_number || step.number || 0,
        title: step.title || '',
        description: step.description || '',
      }));

    if (this.instructionsSectionData?.steps && Array.isArray(this.instructionsSectionData.steps)) {
      return mapSteps(this.instructionsSectionData.steps);
    }
    if (
      page?.sections?.instruction_section?.steps &&
      Array.isArray(page.sections.instruction_section.steps)
    ) {
      return mapSteps(page.sections.instruction_section.steps);
    }
    if (page?.instructions?.steps && Array.isArray(page.instructions.steps)) {
      return mapSteps(page.instructions.steps);
    }

    return [];
  }

  /**
   * Get promo code label from API data
   */
  getPromoCodeLabel(): string {
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    const couponSection = page?.sections?.coupon_section || page?.section?.coupon || {};
    const nestedCard = couponSection.coupon_card || this.couponCardData || {};
    if (nestedCard.promo_label?.text) {
      return nestedCard.promo_label.text;
    }
    if (couponSection.promoCode?.label) {
      return couponSection.promoCode.label;
    }
    return 'PROMO CODE';
  }

  /**
   * Get promo code note from API data
   */
  getPromoCodeNote(): string {
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    const couponSection = page?.sections?.coupon_section || page?.section?.coupon || {};
    const nestedCard = couponSection.coupon_card || this.couponCardData || {};
    if (nestedCard.availability_label?.text) {
      return nestedCard.availability_label.text;
    }
    if (couponSection.promoCode?.note) {
      return couponSection.promoCode.note;
    }
    return 'Available for the following washes';
  }

  /**
   * Get coupon tag color from API data
   */
  getCouponTagColor(): string {
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    const couponSection = page?.sections?.coupon_section || page?.section?.coupon || {};
    const nestedCard = couponSection.coupon_card || this.couponCardData || {};
    if (nestedCard.left_tab?.background) {
      return nestedCard.left_tab.background;
    }
    if (couponSection.promoCode?.tagColor) {
      return couponSection.promoCode.tagColor;
    }
    // Fallback to couponCardData or default
    if (this.couponCardData?.color) {
      return this.couponCardData.color;
    }
    if (this.couponCardData?.backgroundColor) {
      return this.couponCardData.backgroundColor;
    }
    return '#1B5BFF';
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
